<template>
  <div class="complex-text-widget-editor">
    <position-editor :values="values" @update-model="updateModel" />

    <controller-section title="Layout">
      <div class="control-row button-row" tabindex="0" @keydown="rollValue('textAlign', ['left', 'center', 'right'], $event)">
        <div class="button" @click="updateModel('textAlign', 'left')" :class="{ selected: values.textAlign === 'left' }">
          <fa icon="fa-align-left"/>
        </div>
        <div class="button" @click="updateModel('textAlign', 'center')" :class="{ selected: values.textAlign === 'center' }">
          <fa icon="fa-align-center"/>
        </div>
        <div class="button" @click="updateModel('textAlign', 'right')" :class="{ selected: values.textAlign === 'right' }">
          <fa icon="fa-align-right" />
        </div>
      </div>
      <div class="control-row button-row" tabindex="0" @keydown="rollValue('direction', ['ltr', 'rtl'], $event)">
        <div class="button" @click="updateModel('direction', 'ltr')" :class="{ selected: values.direction === 'ltr' }">
          <fa icon="fa-caret-right"/>
        </div>
        <div class="button" @click="updateModel('direction', 'rtl')" :class="{ selected: values.direction === 'rtl' }">
          <fa icon="fa-caret-left" />
        </div>
      </div>
      <div class="control-row button-row" tabindex="0" @keydown="rollValue('verticalAlign', ['start', 'center', 'end'], $event)">
        <div class="button" @click="updateModel('verticalAlign', 'start')" :class="{ selected: values.verticalAlign === 'start' }">
          <fa icon="fa-arrows-up-to-line"/>
        </div>
        <div class="button" @click="updateModel('verticalAlign', 'center')" :class="{ selected: values.verticalAlign === 'center' }">
          <fa icon="grip-lines"/>
        </div>
        <div class="button" @click="updateModel('verticalAlign', 'end')" :class="{ selected: values.verticalAlign === 'end' }">
          <fa icon="fa-arrows-down-to-line" />
        </div>
      </div>
      <div class="control-row">
        <controller field-name="zIndex" :value="values.zIndex" prepend="Z" @input="updateModel" append=""></controller>
      </div>
    </controller-section>

    <controller-section title="Parts" :start-visible="true">
      <draggable v-model="values.parts" handle=".handle">
        <div class="part" v-for="(p, index) in values.parts" :key="index">
          <i class="fa fa-align-justify handle"></i>
          <div class="control-row text-row">
            <controller field-name="text" :value="p.text" append="" input-type="text" @input="updatePart(p, ...arguments)"></controller>
          </div>
          <div class="control-row">
            <b-input-group class="control" size="sm" prepend="font">
              <b-form-select :value="p.fontFamily" @input="updatePart(p, 'fontFamily', $event)" :options="availableFonts"></b-form-select>
            </b-input-group>
          </div>
          <div class="control-row">
            <controller field-name="fontSize" :value="p.fontSize" prepend-icon="font" @input="updatePart(p, ...arguments)"></controller>
            <controller field-name="lineHeight" :value="p.lineHeight" prepend-icon="text-height" @input="updatePart(p, ...arguments)"></controller>
          </div>
          <div class="control-row">
            <controller field-name="color" :value="p.color" prepend-icon="paintbrush" input-type="text"
                        @input="updatePart(p, ...arguments)" append="" />
            <controller field-name="background" :value="p.background" prepend-icon="fill" input-type="text"
                        @input="updatePart(p, ...arguments)" append="" />
          </div>
          <div class="control-row">
            <controller field-name="url" :value="p.url" prepend-icon="link" append="" input-type="text" @input="updatePart(p, ...arguments)" />
          </div>
          <div class="control-row">
            <b-button @click="deletePart(p)" variant="danger"><fa icon="fa-trash"/></b-button>
          </div>
        </div>
        <div class="control-row">
          <b-button @click="newText" variant="primary">New Part</b-button>
        </div>
      </draggable>
    </controller-section>

    <div>
      Border Radius
      <b-form-input v-model="values.borderRadius" size="sm" @input="updateModel('borderRadius', $event)"></b-form-input>
    </div>
    <div>
      Padding
      <b-form-input v-model="values.padding" size="sm" @input="updateModel('padding', $event)"></b-form-input>
    </div>
  </div>
</template>
<script>
import draggable from 'vuedraggable';
import Controller from '../Controller.vue';
import ControllerSection from '../ControllerSection.vue';
import PositionEditor from '../PositionEditor.vue';

export default {
  name: 'ComplexTextWidgetEditor',
  components: {
    draggable,
    ControllerSection,
    Controller,
    PositionEditor,
  },
  props: {
    values: {},
  },
  computed: {
    availableFonts() {
      return ['SimplerPro', 'SimplerProBold', 'PauzaBold', 'SimplerPro_Alte-Regular', 'SimplerPro_Alte-Bold', 'SimplerPro_Alte-Black', 'Sunny'];
    },
  },
  methods: {
    updateModel(field, value) {
      this.$emit('update', field, value);
    },
    updatePart(p, field, value) {
      // eslint-disable-next-line no-param-reassign
      p[field] = value;
    },
    deletePart(part) {
      this.values.parts = _.reject(this.values.parts, p => p === part);
    },
    newText() {
      this.values.parts.push({
        text: '',
        color: this.values.parts[0]?.color || '#000000',
        background: this.values.parts[0]?.background || '#FFFFFF',
        fontFamily: this.values.parts[0]?.fontFamily || 'SimplerPro',
        fontSize: this.values.parts[0]?.fontSize || 20,
        lineHeight: this.values.parts[0]?.lineHeight || '',
      });
    },
    rollValue(field, values, event) {
      const currentIndex = _.indexOf(values, this.values[field]);
      const newValue = values[_getValueBetween(0, currentIndex + _getIndexChange(event.code), values.length - 1)];
      this.updateModel(field, newValue);
    },
  },
};

function _getIndexChange(keypress) {
  if (keypress === 'ArrowLeft') {
    return -1;
  }
  if (keypress === 'ArrowRight') {
    return 1;
  }
  return 0;
}

function _getValueBetween(min, value, max) {
  if (value < min) {
    return min;
  }
  if (value > max) {
    return max;
  }
  return value;
}

</script>

<style lang="scss" scoped>
@import '~@riseupil/base-ui/src/scss/riseup-colors';

$button_radius: 4px;

.complex-text-widget-editor {
  .control-row {
    display: flex;
    justify-content: space-between;
    margin-top: 12px;
    align-items: center;
    &.text-row {
      &::v-deep .control input {
        text-align: right;
        direction: rtl;
      }
    }
    div {
      &:not(:last-of-type) {
        margin-right: 10px;
      }
    }
  }

  .part {
    margin-bottom: 24px;
    .handle {
      cursor: grab;
    }
  }

  .button-row {
    border-radius: $button_radius;
    background-color: $riseup_gray_0;
    color: $riseup_black;
    .button {
      width: 100%;
      padding: 2px 0;
      text-align: center;
      &.selected {
        background-color: $riseup_light_blue;
        color: $riseup_white;
      }
      &:first-of-type {
        border-radius: $button_radius 0 0 $button_radius;
      }
      &:last-of-type {
        border-radius: 0 $button_radius $button_radius 0;
      }
    }
  }
}
</style>
